import { useId, type FormEvent, type ReactNode } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import { designTokens } from '../tokens';
import { dialogActionsStyles, dialogContentStyles, dialogHeadingStyles } from './dialogLayout';
import { useDialogContentOverflow } from './useDialogContentOverflow';
import { HelpPopover } from './HelpPopover';

export type DialogSurfaceSize = 'compact' | 'standard' | 'large' | 'wide';
export type DialogSurfaceMobilePresentation = 'dialog' | 'fullscreen';

export type DialogSurfaceProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  help?: ReactNode;
  /** 시각적 제목만으로 용도가 명확하지 않을 때 대화상자의 접근성 이름을 지정합니다. */
  ariaLabel?: string;
  children: ReactNode;
  actions?: ReactNode;
  description?: ReactNode;
  /** title은 설명을 제목과 묶어 leading 옆에 표시하고, below는 별도 행에 표시합니다. */
  descriptionPlacement?: 'below' | 'title';
  titleLeading?: ReactNode;
  titleAdornment?: ReactNode;
  /** 제목 바로 아래에 붙는 경로·보조 정보이며 대화상자 설명과는 분리합니다. */
  titleMeta?: ReactNode;
  headerContent?: ReactNode;
  /** 항상 표시하는 닫기 버튼의 접근성 이름과 Tooltip입니다. */
  closeLabel?: string;
  busy?: boolean;
  /** 닫힌 동안에도 본문 상태와 진행 중 작업을 유지할 때 사용합니다. 수명은 호출자가 관리합니다. */
  keepMounted?: boolean;
  /** 열기 전환이 끝난 뒤 업무별 초점을 이동할 때 사용합니다. */
  onEntered?: () => void;
  onSubmit?: () => void | Promise<void>;
  size?: DialogSurfaceSize;
  mobilePresentation?: DialogSurfaceMobilePresentation;
  contentDividers?: boolean;
  contentMode?: 'flow' | 'fill';
  testId?: string;
};

const dialogWidth: Record<DialogSurfaceSize, number> = {
  compact: designTokens.layout.dialogCompactMaxWidth,
  standard: designTokens.layout.dialogStandardMaxWidth,
  large: designTokens.layout.dialogLargeMaxWidth,
  wide: designTokens.layout.dialogWideMaxWidth,
};

/**
 * 제목·설명·닫기·본문·action과 mobile presentation을 통일하는 공용 modal shell입니다.
 * 업무 문구와 본문은 slot으로 받고 focus trap·focus return·escape 처리는 MUI Dialog에 위임합니다.
 */
export function DialogSurface({
  open,
  onClose,
  title,
  help,
  ariaLabel,
  children,
  actions,
  description,
  descriptionPlacement = 'below',
  titleLeading,
  titleAdornment,
  titleMeta,
  headerContent,
  closeLabel = '팝업 닫기',
  busy = false,
  keepMounted = false,
  onEntered,
  onSubmit,
  size = 'standard',
  mobilePresentation = 'dialog',
  contentDividers = false,
  contentMode = 'flow',
  testId,
}: DialogSurfaceProps) {
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const fullScreen = compact && mobilePresentation === 'fullscreen';
  const generatedId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const titleId = `dialog-surface-${generatedId}-title`;
  const descriptionId = description === undefined ? undefined : `dialog-surface-${generatedId}-description`;
  const descriptionContent = description === undefined ? null : (
    <Typography id={descriptionId} component="div" variant="body2" color="text.secondary" sx={{ gridColumn: descriptionPlacement === 'below' ? (titleLeading === undefined ? 1 : 2) : undefined, mt: `${descriptionPlacement === 'title' ? designTokens.space.xxs : designTokens.space.xs}px`, overflowWrap: 'anywhere' }}>
      {description}
    </Typography>
  );
  const maxWidth = dialogWidth[size];
  const { contentRef, overflowing } = useDialogContentOverflow(actions !== undefined && !contentDividers);
  const surfacePadding = {
    xs: `${designTokens.layout.surfacePaddingCompact}px`,
    sm: `${designTokens.layout.surfacePadding}px`,
  };

  const requestClose = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      keepMounted={keepMounted}
      onClose={requestClose}
      aria-labelledby={ariaLabel === undefined ? titleId : undefined}
      aria-describedby={descriptionId}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      data-testid={testId}
      slotProps={{
        transition: { onEntered },
        paper: {
          // MUI가 자동 생성하는 aria-labelledby 대신 실제 dialog 역할에 이름을 전달합니다.
          ...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel, 'aria-labelledby': undefined }),
          sx: {
            width: fullScreen ? '100%' : {
              xs: `min(${maxWidth}px, calc(100% - ${designTokens.layout.surfacePaddingCompact * 2}px))`,
              sm: `min(${maxWidth}px, calc(100% - ${designTokens.layout.dialogInlineInset * 2}px))`,
            },
            maxWidth: 'none',
            maxHeight: fullScreen ? 'none' : {
              xs: `calc(100% - ${designTokens.layout.surfacePaddingCompact * 2}px)`,
              sm: `calc(100% - ${designTokens.layout.dialogBlockInset * 2}px)`,
            },
            m: fullScreen ? 0 : surfacePadding,
            // 본문만 스크롤하고, 초점 이동으로 팝업 외곽이 밀리지 않게 합니다.
            overflow: 'clip',
          },
        },
      }}
    >
      <Box
        component={onSubmit === undefined ? 'div' : 'form'}
        noValidate={onSubmit === undefined ? undefined : true}
        aria-busy={busy}
        onSubmit={onSubmit === undefined ? undefined : (event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (!busy) void onSubmit();
        }}
        sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}
      >
        <DialogTitle id={`${titleId}-header`} component="div" sx={dialogHeadingStyles(contentDividers)}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `${titleLeading === undefined ? '' : 'auto '}minmax(0, 1fr) auto`,
            alignItems: 'center',
            columnGap: `${designTokens.space.sm}px`,
          }}>
            {titleLeading === undefined ? null : <Box sx={{ display: 'flex', alignItems: 'center' }}>{titleLeading}</Box>}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ minWidth: 0, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography id={titleId} component="h2" variant="h5" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  {title}
                </Typography>
                {titleAdornment}
                {help === undefined ? null : <HelpPopover title={typeof title === 'string' ? title : '팝업 안내'}>{help}</HelpPopover>}
              </Stack>
              {descriptionPlacement === 'title' ? descriptionContent : null}
            </Box>
            <Tooltip title={closeLabel}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton type="button" aria-label={closeLabel} disabled={busy} onClick={requestClose}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
            </Tooltip>
            {titleMeta === undefined ? null : <Box sx={{ minWidth: 0, gridColumn: titleLeading === undefined ? 1 : 2, mt: `${designTokens.space.xxs}px` }}>{titleMeta}</Box>}
            {descriptionPlacement === 'below' ? descriptionContent : null}
            {headerContent === undefined ? null : <Box sx={{ minWidth: 0, gridColumn: '1 / -1', mt: `${designTokens.layout.surfacePadding}px` }}>{headerContent}</Box>}
          </Box>
        </DialogTitle>
        <DialogContent
          ref={contentRef}
          dividers={contentDividers}
          sx={dialogContentStyles({ dividers: contentDividers, actions: actions !== undefined, fill: contentMode === 'fill' })}
        >
          {children}
        </DialogContent>
        {actions === undefined ? null : (
          <DialogActions disableSpacing sx={dialogActionsStyles({ dividers: contentDividers, scrollDivider: overflowing })}>
            {actions}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
}
